import { useCallback, useState } from 'react';

import { type JoinedWorkspaceRelation } from '../../contracts';

export interface SwitchWorkspaceGuardPorts {
  /** Whether a recording is active right now — the app owns every layer that can hold one. */
  isRecordingActive: () => Promise<boolean>;
  /** Performs the switch once it is allowed. */
  performSwitch: (workspace: JoinedWorkspaceRelation) => void;
}

export interface SwitchWorkspaceGuard {
  /** `true` while a refused switch is being explained to the user. */
  isRefused: boolean;
  /** Switches now if nothing is recording, otherwise refuses and raises `isRefused`. */
  requestSwitch: (workspace: JoinedWorkspaceRelation) => Promise<void>;
  dismiss: () => void;
}

/**
 * Guards a workspace switch behind a recording check. Switching re-stamps
 * every request with the new workspace, and on desktop the native recorder
 * outlives the document, so a recording in flight would have its remaining
 * uploads misattributed; the switch is refused until the recording has been
 * ended through the recording UI.
 *
 * Both sides cross as ports: the recording state lives app-side (Redux timer,
 * Chronicle session, offline capture, the desktop's native probe) and so does
 * the switch itself. This hook only sequences them.
 *
 * A rejecting `isRecordingActive` fails open and switches: the desktop probe
 * already fails open on a wedged IPC so the user is never trapped, and a
 * dropped click would leave them unable to switch at all.
 */
export function useSwitchWorkspaceGuard({
  isRecordingActive,
  performSwitch,
}: SwitchWorkspaceGuardPorts): SwitchWorkspaceGuard {
  const [isRefused, setIsRefused] = useState(false);

  const requestSwitch = useCallback(
    async (workspace: JoinedWorkspaceRelation) => {
      if (await isRecordingActive().catch(() => false)) {
        setIsRefused(true);
        return;
      }
      performSwitch(workspace);
    },
    [isRecordingActive, performSwitch]
  );

  const dismiss = useCallback(() => setIsRefused(false), []);

  return { isRefused, requestSwitch, dismiss };
}
